import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import {
  Search,
  GraduationCap,
  CheckCircle,
  Clock,
  Plus,
  Eye,
  FileText,
  Award,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Inbox
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { admissionsAPI, agentAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';

const APPLICATION_STATUSES = [
  'Assigned', 'Docs Pending', 'Docs Verified', 'Submitted',
  'Under Review', 'Offer Received', 'Accepted', 'Visa Processing',
  'Completed', 'Rejected'
];

const STATUS_COLORS = {
  'Assigned': 'bg-blue-100 text-blue-700',
  'Docs Pending': 'bg-amber-100 text-amber-700',
  'Docs Verified': 'bg-cyan-100 text-cyan-700',
  'Submitted': 'bg-indigo-100 text-indigo-700',
  'Under Review': 'bg-purple-100 text-purple-700',
  'Offer Received': 'bg-emerald-100 text-emerald-700',
  'Accepted': 'bg-green-100 text-green-700',
  'Visa Processing': 'bg-orange-100 text-orange-700',
  'Completed': 'bg-green-200 text-green-800',
  'Rejected': 'bg-red-100 text-red-700'
};

export default function AgentAdmissions() {
  const navigate = useNavigate();
  const basePath = useRoleBasePath();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [universityFilter, setUniversityFilter] = useState('');
  const [intakeFilter, setIntakeFilter] = useState('');

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [students, setStudents] = useState([]);
  const [newApp, setNewApp] = useState({
    studentId: '',
    universityName: '',
    programName: '',
    programLevel: '',
    intake: ''
  });

  const fetchApplications = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (universityFilter) params.university = universityFilter;
      if (intakeFilter) params.intake = intakeFilter;
      if (searchTerm) params.search = searchTerm;

      const result = await admissionsAPI.getAgentList(params);
      setApplications(result.data || []);
      setPagination(result.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      console.error('Failed to fetch applications:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, universityFilter, intakeFilter, searchTerm]);

  useEffect(() => {
    fetchApplications(1);
  }, [fetchApplications]);

  const fetchStudents = async () => {
    try {
      const res = await agentAPI.getStudentsList();
      setStudents(res.students || res.data || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
    }
  };

  const handleCreateOpen = () => {
    fetchStudents();
    setNewApp({ studentId: '', universityName: '', programName: '', programLevel: '', intake: '' });
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!newApp.studentId || !newApp.universityName || !newApp.programName || !newApp.intake) {
      toast.error('Please fill in all required fields');
      return;
    }
    setCreating(true);
    try {
      await admissionsAPI.create(newApp);
      toast.success('Application created successfully');
      setCreateOpen(false);
      fetchApplications(1);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create application');
    } finally {
      setCreating(false);
    }
  };

  // Stats
  const totalApps = pagination.total;
  const activeCount = applications.filter(a => !['Completed', 'Rejected'].includes(a.status)).length;
  const offerCount = applications.filter(a => a.status === 'Offer Received').length;
  const completedCount = applications.filter(a => a.status === 'Completed').length;

  const getStatusBadge = (status) => {
    const colorClass = STATUS_COLORS[status] || 'bg-gray-100 text-gray-700';
    return <Badge className={colorClass}>{status}</Badge>;
  };

  const getInitials = (student) => {
    if (!student || !student.firstName) return '??';
    return `${student.firstName?.[0] || ''}${student.lastName?.[0] || ''}`;
  };

  return (
    <AgentLayout>
      <div className="space-y-6" data-testid="agent-admissions">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-outfit font-bold text-gray-900">Admissions</h1>
            <p className="text-gray-500">Manage university applications for your students</p>
          </div>
          <Button onClick={handleCreateOpen} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            New Application
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{totalApps}</p>
                <p className="text-sm text-gray-500">Total</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
                <p className="text-sm text-gray-500">Active</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{offerCount}</p>
                <p className="text-sm text-gray-500">Offers</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{completedCount}</p>
                <p className="text-sm text-gray-500">Completed</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4 bg-white border-0 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by student name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-200"
              />
            </div>
            <Select value={statusFilter} onValueChange={(val) => setStatusFilter(val === 'all' ? '' : val)}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {APPLICATION_STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="University..."
              value={universityFilter}
              onChange={(e) => setUniversityFilter(e.target.value)}
              className="w-full md:w-48 border-gray-200"
            />
            <Input
              placeholder="Intake..."
              value={intakeFilter}
              onChange={(e) => setIntakeFilter(e.target.value)}
              className="w-full md:w-36 border-gray-200"
            />
          </div>
        </Card>

        {/* Table */}
        {loading ? (
          <Card className="p-12 bg-white border-0 shadow-sm flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
          </Card>
        ) : applications.length === 0 ? (
          <Card className="p-12 bg-white border-0 shadow-sm text-center">
            <Inbox className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">No Applications Found</h3>
            <p className="text-gray-500 mt-1">Create your first application to get started.</p>
            <Button onClick={handleCreateOpen} className="mt-4 bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              New Application
            </Button>
          </Card>
        ) : (
          <Card className="bg-white border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="admissions-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Student</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">University</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Program</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Intake</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Assigned By</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-gray-500">Last Updated</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {applications.map((app, index) => (
                    <motion.tr
                      key={app.applicationId}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.03 }}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-xs">
                            {getInitials(app.student)}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">
                            {app.student?.firstName} {app.student?.lastName}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-gray-900 text-sm">{app.universityName}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600 text-sm">{app.programName}</td>
                      <td className="py-4 px-6 text-gray-600 text-sm">{app.intake}</td>
                      <td className="py-4 px-6">{getStatusBadge(app.status)}</td>
                      <td className="py-4 px-6">
                        <Badge variant="outline" className="text-xs capitalize">{app.assignedBy}</Badge>
                      </td>
                      <td className="py-4 px-6 text-gray-500 text-sm">
                        {new Date(app.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`${basePath}/admissions/${app.applicationId}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => fetchApplications(pagination.page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Page {pagination.page} of {pagination.totalPages}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => fetchApplications(pagination.page + 1)}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Create Application Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                New University Application
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Student *</Label>
                <Select value={newApp.studentId} onValueChange={(val) => setNewApp(prev => ({ ...prev, studentId: val }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select a student" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map(s => (
                      <SelectItem key={s.studentId} value={s.studentId}>
                        {s.firstName} {s.lastName} {s.email ? `(${s.email})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>University Name *</Label>
                <Input
                  value={newApp.universityName}
                  onChange={(e) => setNewApp(prev => ({ ...prev, universityName: e.target.value }))}
                  placeholder="e.g. University of Toronto"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Program Name *</Label>
                <Input
                  value={newApp.programName}
                  onChange={(e) => setNewApp(prev => ({ ...prev, programName: e.target.value }))}
                  placeholder="e.g. Computer Science"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Program Level</Label>
                <Select value={newApp.programLevel} onValueChange={(val) => setNewApp(prev => ({ ...prev, programLevel: val }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select level (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Undergraduate Program">Undergraduate</SelectItem>
                    <SelectItem value="Graduate Program">Graduate</SelectItem>
                    <SelectItem value="Postgraduate Program">Postgraduate</SelectItem>
                    <SelectItem value="Diploma">Diploma</SelectItem>
                    <SelectItem value="Certificate">Certificate</SelectItem>
                    <SelectItem value="Doctoral Program">Doctoral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Intake *</Label>
                <Input
                  value={newApp.intake}
                  onChange={(e) => setNewApp(prev => ({ ...prev, intake: e.target.value }))}
                  placeholder="e.g. Fall 2025"
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">
                  {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Create Application
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AgentLayout>
  );
}
